import React, { useCallback, useMemo, useState } from "react";

import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import Tooltip from "@mui/material/Tooltip";

import UploadFileIcon from "@mui/icons-material/UploadFile";

import { ButtonUI } from "../../../../../../../Common";

import { setPrevState } from "../../../../../../../../util/common";
import { regexFilePath } from "../../../../../../../../util/regularExpress";
import { browserStateVar, JOB_PURPOSE_EXCHANGE } from "../../../../../utils/JobData";
import S3FileBrowser from "../../../../../../Browsers/S3FileBrowser";
import AzureFileBrowser from "../../../../../../Browsers/AzureFileBrowser";
import SSHFileBrowser from "../../../../../../Browsers/SSHFileBrowser";
import RegexHelp from "../../../../../DataExchange/DataExchangeScheduler/DestinationForm/RegexHelp";

const BrowseInput = ({ exchangeJobState, setExchangeJobState, $destinationList, jobPurpose, destinationType }) => {
  const { destination, sourceType, targetPath } = exchangeJobState;

  const [tmpBucket, setTmpBucket] = useState("");
  const [openBrowser, setOpenBrowser] = useState({
    sftpBrowser: false,
    azureBrowser: false,
    s3Browser: false,
  });

  const [openRegexHelp, setOpenRegexHelp] = useState(false);

  const allowRename = useMemo(() => {
    if (sourceType?.toLowerCase() === "s3" && (destinationType === "s3" || destinationType === "sftp")) return true;
    if (destinationType === "s3" && (sourceType?.toLowerCase() === "sftp" || sourceType?.toLowerCase() === "azure"))
      return true;

    return false;
  }, [destinationType, sourceType]);

  const handleBrowser = useCallback((browser, val) => {
    const prop = browserStateVar[browser];
    setOpenBrowser((prev) => {
      const newState = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = key === prop ? val : false;
      });

      return newState;
    });
  }, []);

  const handleBrowseClick = useCallback(() => {
    const locationDetail = $destinationList.find((locationDetail) => locationDetail.Stage_id === destination);

    if (locationDetail) {
      const Detail = locationDetail.Detail;
      setTmpBucket(Detail.url);
      handleBrowser(Detail.type.toLowerCase(), true);
    }
  }, [$destinationList, destination, handleBrowser]);

  const handleS3Browser = useCallback((val) => handleBrowser("s3", val), [handleBrowser]);
  const handleAzureBrowser = useCallback((val) => handleBrowser("azure", val), [handleBrowser]);
  const handleSFTPBrowser = useCallback((val) => handleBrowser("sftp", val), [handleBrowser]);

  const setPath = useCallback(
    (pathValue) => setPrevState(setExchangeJobState, { targetPath: pathValue, bucket: tmpBucket }),
    [setExchangeJobState, tmpBucket]
  );

  return (
    <>
      <FormControl
        sx={{
          width: { xs: "100%", sm: "300px" },
          m: 1,
          ml: 0,
        }}
      >
        <TextField
          value={targetPath}
          required
          label="Folder path"
          inputProps={{ maxLength: 255 }}
          onChange={(e) => {
            if (e.target.value === "") setPrevState(setExchangeJobState, { targetPath: "" });
            else if (regexFilePath(e.target.value)) setPrevState(setExchangeJobState, { targetPath: e.target.value });
          }}
          onBlur={() => {
            if (targetPath && targetPath[0] !== "/")
              setPrevState(setExchangeJobState, { targetPath: "/" + targetPath });
            if (targetPath && !targetPath.endsWith("/"))
              setPrevState(setExchangeJobState, { targetPath: targetPath + "/" });
          }}
          helperText={`Not allowed characters \\:;~*'?"<>|`}
        />
      </FormControl>
      <ButtonUI
        disabled={!(destination && sourceType)}
        color="secondary"
        component="span"
        variant="contained"
        endIcon={<UploadFileIcon />}
        sx={{
          marginTop: "18px",
        }}
        onClick={handleBrowseClick}
      >
        Browse
      </ButtonUI>

      {allowRename && jobPurpose === JOB_PURPOSE_EXCHANGE && (
        <>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exchangeJobState.renameFileRegex}
                    onChange={(e) =>
                      setPrevState(setExchangeJobState, { renameFileRegex: !exchangeJobState.renameFileRegex })
                    }
                  />
                }
                sx={{ mr: 1 }}
                label="Rename files using REGEX"
              />
            </FormGroup>
            <Tooltip title="Help" placement="top" arrow>
              <IconButton color="info" size="small" onClick={() => setOpenRegexHelp((prev) => !prev)}>
                <HelpOutlineOutlinedIcon sx={{ cursor: "pointer", fontSize: "20px" }} />
              </IconButton>
            </Tooltip>
          </Box>

          {exchangeJobState.renameFileRegex && (
            <>
              <FormControl sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}>
                <TextField
                  value={exchangeJobState.regexMatchPattern}
                  required
                  label="Match Pattern"
                  inputProps={{ maxLength: 255 }}
                  onChange={(e) => {
                    if (e.target.value === "") setPrevState(setExchangeJobState, { regexMatchPattern: "" });
                    else setPrevState(setExchangeJobState, { regexMatchPattern: e.target.value });
                  }}
                  helperText={`Enter a valid regex pattern.`}
                />
              </FormControl>
              <FormControl sx={{ width: { xs: "100%", sm: "300px" }, m: 1, ml: 0 }}>
                <TextField
                  value={exchangeJobState.regexReplacePattern}
                  required
                  label="Replace pattern"
                  inputProps={{ maxLength: 255 }}
                  onChange={(e) => {
                    if (e.target.value === "") setPrevState(setExchangeJobState, { regexReplacePattern: "" });
                    else setPrevState(setExchangeJobState, { regexReplacePattern: e.target.value });
                  }}
                />
              </FormControl>
            </>
          )}
        </>
      )}

      {openRegexHelp && <RegexHelp onClose={() => setOpenRegexHelp(false)}></RegexHelp>}

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={exchangeJobState.unzipFiles}
                onChange={(e) => setPrevState(setExchangeJobState, { unzipFiles: !exchangeJobState.unzipFiles })}
              />
            }
            sx={{ mr: 1 }}
            label="Unzip Files"
          />
        </FormGroup>
      </Box>

      {openBrowser.s3Browser && (
        <S3FileBrowser
          setOpenFileBrowser={handleS3Browser}
          url={tmpBucket}
          path={exchangeJobState.targetPath}
          setPath={setPath}
          stage={exchangeJobState.destination}
          locationName={
            exchangeJobState.destination &&
            exchangeJobState.locationList?.find((loc) => loc.Stage_id === exchangeJobState.destination).Name
          }
          folderSelection
        />
      )}
      {openBrowser.azureBrowser && (
        <AzureFileBrowser
          container={exchangeJobState.desinationContainer}
          setOpenFileBrowser={handleAzureBrowser}
          stage={exchangeJobState.destination}
          locationName={
            exchangeJobState.destination &&
            exchangeJobState.locationList?.find((loc) => loc.Stage_id === exchangeJobState.destination).Name
          }
          path={`${
            exchangeJobState.targetPath
              ? "/" +
                exchangeJobState.targetPath
                  .split(",/")
                  .map((p, i) => `${exchangeJobState.desinationContainer}${i === 0 ? "" : "/"}${p}`)
                  .join(",/")
              : `/${exchangeJobState.desinationContainer}/`
          }`}
          setPath={(pathValue) => {
            setPrevState(setExchangeJobState, {
              targetPath: pathValue
                .replace(new RegExp(`^/${exchangeJobState.desinationContainer}`), "")
                .replace(new RegExp(`,/${exchangeJobState.desinationContainer}`, "g"), ","),
            });
          }}
          folderSelection
        />
      )}
      {openBrowser.sftpBrowser && (
        <SSHFileBrowser
          setOpenFileBrowser={handleSFTPBrowser}
          stage={exchangeJobState.destination}
          locationName={
            exchangeJobState.destination &&
            exchangeJobState.locationList?.find((loc) => loc.Stage_id === exchangeJobState.destination).Name
          }
          url={tmpBucket || "/"}
          pathStr={exchangeJobState.targetPath}
          setPath={setPath}
          folderSelection
        />
      )}
    </>
  );
};

export default BrowseInput;
